/**
 * External dependencies
 */
import React, { useRef } from "react";
import { Box, Text } from "@towersystems/towersystems-design-system";
import { useDrag, useDrop } from "react-dnd";
import classnames from "classnames";

/**
 * Internal dependencies
 */
import Icon from "components/icon";
import Button from "components/button";
import { ItemTypes } from "./item-types";

const Item = ({ label, id, remove, index, moveItem }) => {
  const ref = useRef(null);

  const [, drop] = useDrop({
    accept: ItemTypes.ITEM,
    hover(item, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;
      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }
      // Determine rectangle on screen
      const hoverBoundingRect = ref.current.getBoundingClientRect();
      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      // Determine mouse position
      const clientOffset = monitor.getClientOffset();
      // Get pixels to the top
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%
      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      // Time to actually perform the action
      moveItem(dragIndex, hoverIndex);
      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    item: { type: ItemTypes.ITEM, id, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const handleOnRemove = () => {
    remove(id);
  };

  drag(drop(ref));

  const classes = classnames("moveable-stack__item", {
    "is-dragging": isDragging,
  });

  return (
    <div ref={ref} className={classes}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="spaceBetween"
        textAlign="left"
        padding="small"
        className="moveable-stack__item-inner"
      >
        <Box display="flex" alignItems="center">
          <Icon name="GrDrag" marginRight={10} />
          <Text size="small">{label}</Text>
        </Box>
        {remove && (
          <Box>
            <Button compact onClick={handleOnRemove} icon>
              <Icon name="IoIosClose" />
            </Button>
          </Box>
        )}
      </Box>
    </div>
  );
};

export default Item;
